import { Handle, Position, type Node, type NodeProps } from "@xyflow/react";
import { IconAlertTriangle, IconBolt, IconLock } from "@tabler/icons-react";
import { cn } from "@/lib/cn";
import type { Notificacion } from "../../services/notificaciones-service";
import { ETIQUETA_CANAL } from "../../utils/lienzo";
import { resumenDestinatarios } from "../../utils/etiquetas";
import { ICONO_CANAL } from "./iconos";

export type NodoEventoData = { nombre: string; categoria: string; codigo: string; enCatalogo: boolean };
export type NodoNotificacionData = { notificacion: Notificacion; avisos: string[] };

export type NodoEventoFlow = Node<NodoEventoData, "evento">;
export type NodoNotificacionFlow = Node<NodoNotificacionData, "notificacion">;

const CAJA = "w-64 rounded-lg border bg-card px-3 py-2.5 shadow-sm transition-colors";

/** Disparador: un evento de integración del catálogo. Solo tiene salida (a la derecha). */
export function NodoEvento({ data, selected }: NodeProps<NodoEventoFlow>) {
  return (
    <div className={cn(CAJA, selected ? "border-multiva-600 ring-2 ring-multiva-600/30" : "border-border", !data.enCatalogo && "border-peligro-600")}>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-advertencia-700 dark:text-advertencia-300">
        <IconBolt size={12} /> Evento · {data.categoria}
      </div>
      <div className="mt-1 text-sm font-medium leading-snug">{data.nombre}</div>
      {!data.enCatalogo ? <div className="mt-1 text-xs text-peligro-600">No existe en el catálogo</div> : null}
      <Handle type="source" position={Position.Right} className="!size-3 !border-2 !border-card !bg-advertencia-600" />
    </div>
  );
}

/** Acción: una notificación por un canal. Solo tiene entrada (a la izquierda). */
export function NodoNotificacion({ data, selected }: NodeProps<NodoNotificacionFlow>) {
  const n = data.notificacion;
  const Icono = ICONO_CANAL[n.canal];
  const detalle = n.canal === "Webhook" ? n.url || "Sin URL" : resumenDestinatarios(n.destinatarios);
  return (
    <div
      className={cn(
        CAJA,
        selected ? "border-multiva-600 ring-2 ring-multiva-600/30" : "border-border",
        !n.activa && "opacity-60",
      )}
    >
      <Handle type="target" position={Position.Left} className="!size-3 !border-2 !border-card !bg-multiva-600" />
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-multiva-700 dark:text-multiva-300">
        <Icono size={12} /> {ETIQUETA_CANAL[n.canal]}
        {n.obligatoria ? <IconLock size={11} className="ml-auto" aria-label="Obligatoria" /> : null}
      </div>
      <div className="mt-1 truncate text-sm font-medium">{n.nombre || "Sin nombre"}</div>
      <div className="truncate text-xs text-muted-foreground">{detalle}</div>
      {!n.activa ? <div className="mt-1 text-xs text-muted-foreground">Inactiva</div> : null}
      {data.avisos.length > 0 ? (
        <div className="mt-1.5 flex items-start gap-1 text-xs text-advertencia-700 dark:text-advertencia-300" title={data.avisos.join("\n")}>
          <IconAlertTriangle size={12} className="mt-0.5 shrink-0" />
          <span className="truncate">{data.avisos[0]}{data.avisos.length > 1 ? ` (+${data.avisos.length - 1})` : ""}</span>
        </div>
      ) : null}
    </div>
  );
}

export const TIPOS_DE_NODO = { evento: NodoEvento, notificacion: NodoNotificacion };
